import {
  DocumentTypesInfiniteSelectField,
  FloatingFormItem,
  InputDate,
  InputDecimal,
  InputText,
} from '@/components/_common';
import { useAccountReceivableRenegotiateInstallment } from '@/core/domain/financial/AccountReceivable/AccountReceivable.hooks';
import {
  AccountReceivableSituation,
  AccountRecivableRenegotiateInstallmentFormValues,
} from '@/core/domain/financial/AccountReceivable/AccountReceivable.types';
import { handleInstallmentAlreadyEdited } from '@/core/domain/financial/AccountReceivable/AccountRecivable.errors';
import { useDefaultEmptyFieldError, useDisclosure } from '@/hooks';
import { UseDisclosureActions } from '@/hooks/useDisclosure';
import { useAccountReceivableContext } from '@/pages/FinancialPages/BillToReceivePage/ReleasesPage/ReleasesDetailPage';
import Authorities from '@/types/Authority';
import { MathRound } from '@/utils/functions/MathRound';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { DateTimeFormat } from '@/utils/moment';
import { Button, Col, Form, Modal, Row } from 'antd';
import { AxiosError } from 'axios';
import moment from 'moment';
import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';

interface Props {
  handleChangeAndOpenModal: (
    actions: UseDisclosureActions,
    loading: string,
  ) => void;
  loading?: boolean;
}

const AccountReceivableRenegotiateInstallment: React.FC<Props> = ({
  handleChangeAndOpenModal,
  loading,
}) => {
  const history = useHistory();
  const [form] =
    Form.useForm<AccountRecivableRenegotiateInstallmentFormValues>();
  const { accountReceivable, currentInstallment } =
    useAccountReceivableContext();
  const [modalOpened, modalAction] = useDisclosure(false);

  const renegotiateInstallment = useAccountReceivableRenegotiateInstallment();
  const ruleRequired = useDefaultEmptyFieldError();

  useEffect(() => {
    const discountIncrease = accountReceivable?.installmentReceivables
      .filter(
        (installment) =>
          installment.situation === AccountReceivableSituation.open,
      )
      .reduce((acc, curr) => (acc += curr.discountIncrease), 0);

    const valueTotalInstallments = accountReceivable?.installmentReceivables
      .filter(
        (installment) =>
          installment.situation === AccountReceivableSituation.open,
      )
      .reduce((acc, curr) => (acc += MathRound(curr.installmentValue, 2)), 0);

    const valueTotal = (valueTotalInstallments ?? 0) + (discountIncrease ?? 0);

    form.setFieldsValue({
      fromThatDate: moment(),
      quantityInstallments: 1,
      intervalDays: 30,
      discountIncrease,
      valueTotal,
      valueTotalInstallments,
    });
  }, [accountReceivable, currentInstallment, modalOpened]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      if (
        accountReceivable &&
        accountReceivable.installmentReceivables[currentInstallment]
      ) {
        const response = await renegotiateInstallment.mutateAsync({
          ...values,
          accountId: accountReceivable.id,
          fromThatDate: values.fromThatDate.format(DateTimeFormat),
          quantityInstallments: Number(values.quantityInstallments),
          valueTotal: Number(values.valueTotal.toFixed(2)),
          installmentReceiveId:
            accountReceivable.installmentReceivables[currentInstallment].id,
          currentSituation:
            accountReceivable.installmentReceivables[currentInstallment]
              .situation,
        });

        history.push(
          `/app/financial/billToReceiver/releaseBillToReceiver/${response.accountId}/installment/${response.installmentId}`,
        );

        handleCancel();
      }
    } catch (err: unknown) {
      const error = err as AxiosError;

      if (error.response?.data) {
        if (handleInstallmentAlreadyEdited(error)) {
          modalAction.close();
        }
      }
    }
  };

  function handleCancel() {
    form.resetFields();
    modalAction.close();
  }

  return (
    <>
      <Button
        disabled={
          !hasPermissions([
            Authorities.FINANCIAL_ACCOUNT_RECEIVABLE_RENEGOTIATE,
          ])
        }
        block
        type="primary"
        onClick={() => handleChangeAndOpenModal(modalAction, 'renegotiate')}
        loading={loading}
      >
        Renegociar
      </Button>

      <Modal
        title="Renegociação"
        onOk={handleSubmit}
        onCancel={handleCancel}
        open={modalOpened}
        confirmLoading={renegotiateInstallment.isLoading}
        okText="Renegociar"
        width={860}
      >
        <Form
          form={form}
          onValuesChange={(
            valuesChanged: AccountRecivableRenegotiateInstallmentFormValues,
            fieldsChanged: AccountRecivableRenegotiateInstallmentFormValues,
          ) => {
            if (valuesChanged.discountIncrease) {
              form.setFieldsValue({
                valueTotal:
                  (fieldsChanged.valueTotalInstallments ?? 0) +
                  (fieldsChanged.discountIncrease ?? 0),
              });
            } else {
              form.setFieldsValue({
                valueTotal:
                  (fieldsChanged.valueTotalInstallments ?? 0) +
                  (fieldsChanged.discountIncrease ?? 0),
              });
            }
          }}
        >
          <Row gutter={[24, 24]}>
            <Col span={24} sm={12} lg={6}>
              <FloatingFormItem name="valueTotalInstallments" label="Valor">
                <InputDecimal disabled />
              </FloatingFormItem>
            </Col>

            <Col span={24} sm={12} lg={6}>
              <FloatingFormItem name="discountIncrease" label="Desconto/Acres.">
                <InputDecimal />
              </FloatingFormItem>
            </Col>

            <Col span={24} sm={12} lg={6}>
              <FloatingFormItem
                name="valueTotal"
                label="Total parcelas abertas"
              >
                <InputDecimal disabled />
              </FloatingFormItem>
            </Col>

            <Col span={24} sm={12} lg={6}>
              <FloatingFormItem
                name="fromThatDate"
                label="A partir de"
                rules={[ruleRequired]}
              >
                <InputDate />
              </FloatingFormItem>
            </Col>

            <Col span={24} sm={12} lg={6}>
              <FloatingFormItem
                name="quantityInstallments"
                label="Parcelas"
                rules={[ruleRequired]}
              >
                <InputText onlyNumber />
              </FloatingFormItem>
            </Col>

            <Col span={24} sm={12} lg={6}>
              <FloatingFormItem
                name="intervalDays"
                label="Intervalo (dias)"
                rules={[ruleRequired]}
              >
                <InputText onlyNumber />
              </FloatingFormItem>
            </Col>

            <Col span={24} sm={12} lg={12}>
              <FloatingFormItem
                name="documentTypeId"
                label="Tipo de documento"
                rules={[ruleRequired]}
              >
                <DocumentTypesInfiniteSelectField
                  queryFilter={{ active: true }}
                />
              </FloatingFormItem>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default AccountReceivableRenegotiateInstallment;
